/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectRoleSelect.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectRoleSelect - Автокомплит роли с цветным чипом и шкалой загрузки.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: RoleWorkloadBar в списке ролей вместо текстового процента.
 * LAST_CHANGE: 2026-07-14 - UX: цветные чипы ролей в списке и выбранном значении.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import RoleWorkloadBar from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';

export function formatProjectRoleLabel(role) {
  return role.name;
}

function RoleChip({ shortName, name }) {
  const color = getColorByName(name || shortName);
  return (
    <Chip
      label={shortName}
      size="small"
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: '0.7rem',
        bgcolor: color,
        color: '#fff',
        mr: 1,
      }}
    />
  );
}

/* START_METHOD_ProjectRoleSelect */
/* START_CONTRACT: ProjectRoleSelect
 * PURPOSE: Автокомплит выбора проектной роли для сотрудника.
 * INPUTS: { label: string, roles: ProjectRole[], value: ProjectRole|null, onChange: (v)=>void, disabled: boolean }.
 * OUTPUTS: { JSX.Element - поле выбора }.
 * SIDE_EFFECTS: нет (делегирует onChange).
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: ProjectRoleSelect
 */
export default function ProjectRoleSelect({
  label,
  roles,
  value,
  onChange,
  disabled,
}) {
  return (
    <Autocomplete
      options={roles}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={formatProjectRoleLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      disabled={disabled}
      renderOption={(props, role) => {
        const { key, ...optionProps } = props;
        const roleColor = getColorByName(role.name);
        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <RoleChip shortName={role.shortName} name={role.name} />
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {role.name}
            </Box>
            <RoleWorkloadBar
              value={role.workloadPercent}
              accentColor={roleColor}
              size="md"
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin="normal"
          size="small"
          required
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {value && (
                    <RoleWorkloadBar
                      value={value.workloadPercent}
                      accentColor={getColorByName(value.name)}
                      size="sm"
                      sx={{ mr: 0.5 }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      noOptionsText="Нет доступных ролей"
    />
  );
}
/* END_METHOD_ProjectRoleSelect */
